import {
  PatientSuggestionsPerDiseaseMetrics,
  QualifiedFeatureFragment,
  Qualifier,
} from "fe-common/graphql";
import { useMemo } from "react";
import { sameQualifers } from "../../dataInputTypes";
import { QualifierWithSelection } from "../refineFeature/refineFeaturePanel";
import { isQualifierEqual } from "../../../util/QualifierComparator";

const suggestionsQualifiers = (
  s: PatientSuggestionsPerDiseaseMetrics,
): Qualifier[] => s.suggestion.qualifiers;

export const useSortQualifiersBySuggestions = (
  qfId: string,
  qualifiers: Qualifier[],
  selectedQualifier: Qualifier[],
  causedByDisease?: (PatientSuggestionsPerDiseaseMetrics & {
    disease: QualifiedFeatureFragment;
  })[],
): QualifierWithSelection[] => {
  const hasQualifiers = !!qualifiers.length;
  return useMemo(() => {
    let sorted = [];
    if (causedByDisease) {
      //ordered by causedByDisease's maximum if exists
      const sortedCausedByDisease = causedByDisease.sort(
        (a, b) => b.findingGivenDisease - a.findingGivenDisease,
      );

      const getQualifierSuggestion = (
        q: Qualifier,
      ): PatientSuggestionsPerDiseaseMetrics & {
        disease: QualifiedFeatureFragment;
      } =>
        sortedCausedByDisease.find((prob) =>
          suggestionsQualifiers(prob).some((q1) => sameQualifers(q1, q)),
        );

      const suggestedQualifiers = qualifiers.filter(getQualifierSuggestion);
      const otherQualifiers = qualifiers.filter(
        (q) => !getQualifierSuggestion(q),
      );

      suggestedQualifiers.sort((q1, q2) => {
        const a = getQualifierSuggestion(q1);
        const b = getQualifierSuggestion(q2);
        return (
          ((b && b.findingGivenDisease) || -Infinity) -
          ((a && a.findingGivenDisease) || -Infinity)
        );
      });

      sorted = suggestedQualifiers.concat(otherQualifiers);
    } else {
      sorted = qualifiers;
    }

    const qs: QualifierWithSelection[] = sorted.map((q) => ({
      qualifier: q,
      selected: selectedQualifier?.some((q1) => isQualifierEqual(q, q1)),
      negated: selectedQualifier?.some((q1) => isQualifierEqual(q, q1))
        ? selectedQualifier?.find((q1) => isQualifierEqual(q, q1)).negate
        : null,
    }));

    const selected = qs.filter((q) => q.selected);
    const unSelected = qs.filter((q) => !q.selected);
    return [...selected, ...unSelected];
    //We want the order to remain between selection, so order only once per QF
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qfId, hasQualifiers]);
};
